import { OAuthService } from 'angular-oauth2-oidc';
import type { StepParams } from '@enlighten/ts-shared/step-api';
import { API_BASE } from '../../constants/general.constants';
import { AppStateService } from '../app-state/app-state.service';

/**
 * Lightweight LLM bridge service for Python and JavaScript step code.
 *
 * Uses raw `fetch()` instead of Angular HttpClient because the bridge
 * runs outside Angular's zone / change-detection cycle. The API matches
 * the non-streaming chat behavior of `LlmService.operationalChat()` (and
 * related streaming variants) so Python code behaves identically in both
 * runtimes.
 */
export class LlmBridgeService {
  constructor(
    private readonly oauth: OAuthService,
    private readonly appState: AppStateService,
  ) {}

  /**
   * EHQ.callLLM, from both bridges as an import method.
   *
   * The shared parser (EHQ-10) has already bound every call shape (enoch's
   * options object, the positional form and Python keywords) to the same
   * params, so this only builds the request.
   *
   * The body is camelCase, as the server's `LlmChatBody` reads it. An explicit
   * null temperature is sent as null, as enoch does; an omitted one is left out.
   *
   * @returns  `{ content, model, usage: { inputTokens, outputTokens }, stopReason }`
   */
  async callLLM(params: StepParams['EHQ.callLLM']): Promise<Record<string, unknown>> {
    const projectId = this.appState.projectId();
    if (!projectId) {
      throw new Error('EHQ.callLLM: no active project');
    }

    const body: Record<string, unknown> = {
      messages: [{ role: 'user', content: params.prompt }],
      stream: false,
    };

    if (params.system !== undefined) body['system'] = params.system;
    if (params.model !== undefined) body['model'] = params.model;
    if (params.maxTokens !== undefined) body['maxTokens'] = params.maxTokens;
    if (params.temperature !== undefined) body['temperature'] = params.temperature;
    if (params.connectionId !== undefined) body['connectionId'] = params.connectionId;

    const token = this.oauth.getAccessToken();
    const response = await fetch(
      `${API_BASE}/project/${projectId}/llm/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`LLM call failed: ${response.status} ${text}`);
    }

    return await response.json();
  }
}
